import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CreateChecklistModal = ({ isOpen, onClose, onSave, sites }) => {
  const [formData, setFormData] = useState({
    checklistName: '',
    description: '',
    siteId: ''
  });

  const [checklistItems, setChecklistItems] = useState([
    { itemDescription: '', isMandatory: true }
  ]);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const siteOptions = sites?.map(site => ({
    value: site?.id,
    label: site?.site_name
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, { itemDescription: '', isMandatory: true }]);
  };

  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index][field] = value;
    setChecklistItems(updated);
  };

  const removeChecklistItem = (index) => {
    if (checklistItems?.length > 1) {
      setChecklistItems(checklistItems?.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.checklistName?.trim()) {
      newErrors.checklistName = 'Checklist name is required';
    }

    if (!formData?.siteId) {
      newErrors.siteId = 'Site is required';
    }

    const validItems = checklistItems?.filter(item => item?.itemDescription?.trim());
    if (validItems?.length === 0) {
      newErrors.items = 'At least one checklist item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setSaving(true);
      const validItems = checklistItems?.filter(item => item?.itemDescription?.trim());
      await onSave({ ...formData, items: validItems });
      setSaving(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({ checklistName: '', description: '', siteId: '' });
    setChecklistItems([{ itemDescription: '', isMandatory: true }]);
    setErrors({});
    setSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="ClipboardList" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create Daily Checklist Template</h2>
              <p className="text-sm text-muted-foreground">Define a reusable checklist for site tasks</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <Input
              label="Checklist Name"
              type="text"
              placeholder="e.g., Daily Safety Checklist"
              value={formData?.checklistName}
              onChange={(e) => handleChange('checklistName', e?.target?.value)}
              error={errors?.checklistName}
              required
            />

            <Select
              label="Assign to Site"
              options={siteOptions}
              value={formData?.siteId}
              onChange={(value) => handleChange('siteId', value)}
              error={errors?.siteId}
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description (Optional)
              </label>
              <textarea
                placeholder="Add a description for this checklist"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-foreground">
                  Checklist Items *
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  iconName="Plus"
                  onClick={addChecklistItem}
                >
                  Add Item
                </Button>
              </div>

              <div className="space-y-3">
                {checklistItems?.map((item, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-background border border-border rounded-lg">
                    <div className="flex-1 space-y-2">
                      <Input
                        type="text"
                        placeholder="e.g., Check fire extinguisher"
                        value={item?.itemDescription}
                        onChange={(e) => updateChecklistItem(index, 'itemDescription', e?.target?.value)}
                      />
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={item?.isMandatory}
                          onChange={(checked) => updateChecklistItem(index, 'isMandatory', checked)}
                          id={`mandatory-${index}`}
                        />
                        <label htmlFor={`mandatory-${index}`} className="text-sm text-muted-foreground cursor-pointer">
                          Required item
                        </label>
                      </div>
                    </div>
                    {checklistItems?.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeChecklistItem(index)}
                        aria-label="Remove item"
                      >
                        <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.items && (
                <p className="text-sm text-destructive mt-2">{errors?.items}</p>
              )}
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-1">Admin-Only Template</p>
                  <p className="text-sm text-muted-foreground">
                    This checklist template can only be created and edited by admins. Workers will be able to complete checklists based on this template.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateChecklistModal;